import { HttpClient } from "@angular/common/http";
import { Component, ElementRef, EventEmitter, Inject, Input, OnInit, Optional, Output, computed, effect, inject, input, viewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { View, Map } from "ol";
import TileLayer from "ol/layer/Tile";
import { ImageWMS, OSM, TileWMS } from "ol/source";
import { forkJoin, switchMap } from "rxjs";
import { ConfigService } from "src/app/utils/config.service";
import { fromFetch } from "rxjs/fetch";
import { MVT } from "ol/format";
import LayerGroup from "ol/layer/Group";
import { applyStyle } from "ol-mapbox-style";
import VectorTileLayer from "ol/layer/VectorTile";
import VectorTile from "ol/source/VectorTile";
import { createXYZ } from "ol/tilegrid";
import { appendParams } from "ol/uri";
import ScaleLine from "ol/control/ScaleLine";
import { defaults as defaultControls } from "ol/control/defaults";
import { Coordinate } from "ol/coordinate";
import { Fill, Stroke, Style } from "ol/style";
import { MapChartService } from "./map-charts.service";
import { LayerGroupDTO } from "./model/LayerGroupDTO";
import { fromLonLat } from "ol/proj";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialogRef } from '@angular/material/dialog';

interface SelectedFeatureInfo {
  featureId: string;
  gmlName: string;
  featureType: string;
}


@Component({
  selector: "app-map-charts",
  standalone: false,
  templateUrl: "./map-charts.component.html",
  styleUrl: "./map-charts.component.scss",
})
export class MapChartsComponent {
  osmBase = input(true);

  wmsBase = input(false);

  vectorIcaoBase = input(false);

  vectorFeature = input(false);

  vectorWms = input(false);

  private _config = ConfigService.getConfig();

  private _mapRef = viewChild<ElementRef>("mapRef");

  selectedFeatures: SelectedFeatureInfo[] = [];

  @Input() config!: {
    geoServerURL: string;
    workspaceName: string;
  };

  @Input() effectiveDate: Date;
  @Input() unName: string;

  mapLayers = [
    // --- BACKGROUND GEOGRAPHY ---
    { id: "gis:osm_international_boundaries", name: "International Boundaries", visible: true },
    { id: "gis:water_polygons", name: "Oceans", visible: true },
    { id: "gis:osm_water_bodies", name: "OSM Water Bodies", visible: true },
    { id: "gis:water_areas", name: "Inland Water Areas", visible: true },
    { id: "gis:osm_shorelines", name: "Shorelines", visible: true },
    { id: "gis:water_lines", name: "Rivers & Streams", visible: true },

    // --- INFRASTRUCTURE & URBAN AREAS (Uncomment to test) ---
    { id: "gis:city_or_large_town", name: "City / Large Town Polygons", visible: true },

    // --- LANDMARKS & CULTURE (Uncomment to test) ---
    { id: "gis:mosques", name: "Mosques", visible: false },
    { id: "gis:pagoda_areas", name: "Pagoda Areas", visible: false },
    { id: "gis:pagoda_points", name: "Pagoda Points", visible: false },
    { id: "gis:stadium_areas", name: "Stadium Areas", visible: false },
    { id: "gis:stadium_points", name: "Stadium Points", visible: false },

    // --- SETTLEMENT POINTS (Uncomment to test) ---
    { id: "gis:village_points", name: "Villages", visible: false },
    { id: "gis:town_points", name: "Towns", visible: false },

    // --- AERODROME INFRASTRUCTURE (Uncomment to test) ---
    { id: "gis:aerodrome_areas", name: "Aerodrome Areas", visible: false },
    { id: "gis:osm_aviation_buildings_poly", name: "Aviation Buildings", visible: false },
    { id: "gis:aerodrome_points", name: "Aerodrome Points", visible: false },
    { id: "gis:arp_points", name: "ARP Points", visible: false },
    { id: "gis:windsock_points", name: "Windsocks", visible: false },

    // --- NAVAIDS & WAYPOINTS (Uncomment to test) ---
    { id: "gis:ndb_points", name: "NDB Navaids", visible: false },
    { id: "gis:vor_points", name: "VOR Navaids", visible: false },
    { id: "gis:dme_points", name: "DME Navaids", visible: false },
    { id: "gis:vordme_points", name: "VOR/DME Navaids", visible: false },
    { id: "gis:tacan_points", name: "TACAN Navaids", visible: false },
    { id: "gis:vortac_points", name: "VORTAC Navaids", visible: false },
    { id: "gis:compass_roses", name: "Compass Roses", visible: false },
    { id: "gis:flyby_int_points", name: "Fly-by Intersections", visible: false },
  ];

  map: Map | null;

  layerGroups: LayerGroupDTO[]; // official layer groups array

  STYLE_LAYERS = [
    "geo-border-line",
    "airspace-line",
    "route-segment-line",
    "route-segment-label",
    "navaid-point-symbol",
    "navaid-tacan-symbol",
    "designated-point-symbol",
    "airport-heliport-symbol",
    "vertical-structure-symbol",
    "runway-centreline-point-symbol",
    "localizer-symbol",
    "airspace-border-labels",
  ];

  constructor(private mapChartService: MapChartService, private router: Router, private route: ActivatedRoute, @Optional() private dialogRef: MatDialogRef<MapChartsComponent>, @Optional()
  @Inject(MAT_DIALOG_DATA)
  public data: {
    unId: string;
    config: {
      geoServerURL: string;
      workspaceName: string;
    };
    unName: string;
    effectiveDate: string;
  } | null
  ) {
    effect(() => {
      if (this._mapRef()) {
        const mapRefElement = this._mapRef();
        this._initMap(mapRefElement);
      }

      if (this.data?.config) {
        this.config = this.data.config;
      }

      // load all official layer groups and add the layers to the map
      this.loadLayerGroups();
    });
  }

  private _initMap(mapRef: ElementRef): void {
    const BASE_OSM_LAYER = new TileLayer({
      source: new OSM(),
    });

    const BASE_VECTOR_ICAO_LAYER = new VectorTileLayer({ declutter: true });

    const BASE_WMS_ICAO_LAYER = new TileLayer({
      source: new TileWMS({
        url: "https://faim-bru.nilacandi.com:8443/geoserver/gis/wms",
        params: {
          LAYERS: this._getActiveLayersString(),
          FORMAT: "image/png",
          TRANSPARENT: true,
          TILED: true,
        },
        serverType: "geoserver",
        transition: 0,
      }),
    });

   
    const BOSNIA_BASE_TILE_LAYER = new TileLayer({
      source: new TileWMS({
         //https://faim-bru.nilacandi.com:8443/geoserver/Bosnia/wms
        url: `${this._config.geoServer.url}${this._config.geoServer.bgMapWorkspace}/wms`,
        params: {
         'LAYERS': [
            'Bosnia:water_polygons',
             'Bosnia:bosnia_international_boundaries',
             'Bosnia:bosnia_cliffs_line',
             'Bosnia:bosnia_town',
             'Bosnia:bosnia_village',
             'Bosnia:bosnia_city_or_large_town',
             'Bosnia:bosnia_lakes_poly',
             'Bosnia:bosnia_railways_line',
             'Bosnia:bosnia_railways_station',
             'Bosnia:bosnia_rivers_line',
             'Bosnia:bosnia_roads_line',
             'Bosnia:bosnia_shorelines',
             'Bosnia:bosnia_buildings_poly'
          ].join(','),
          'VERSION': '1.1.0',
          'FORMAT': 'image/png',
          'TILED': true
        },
        serverType: 'geoserver'
      })
    });

    // Fetch and apply the Mapbox Style JSON
    Promise.all([
      fetch("/assets/map/feature.style.json").then(response => response.json())      
    ]).then(([featureStyle]) => {
      const parsedFeatureStyle = JSON.parse(
        JSON.stringify(featureStyle).replace(/{unId}/g, this.data?.unId)
      );

      /*
       * In "English": “ `applyStyle()` by default will replace your layer's URL with whatever is in the feature.style.json
       * If we want to keep our geoserver source URL, we must explicitly tell me updateSource: false.”
       */

      const UN_LAYER = this.map.getLayers().getArray().find(layer => layer.get('name') === 'un_vector_tile_layer') as VectorTileLayer;
      if (this.config.workspaceName == this._config?.geoServer.unWorkspaceName) {
        this.applyStyles(UN_LAYER, parsedFeatureStyle);
      }
    },
    );

    const layers = {
      //BASE_OSM_LAYER,
      BOSNIA_BASE_TILE_LAYER
      //FEATURES_VECTOR_TILE_LAYER,
    };

    this.map = new Map({
      target: mapRef.nativeElement,
      layers: new LayerGroup({ layers: Object.values(layers) }),
      controls: defaultControls().extend([new ScaleLine({ units: "nautical" })]),
      /* view: new View({
        multiWorld: false,
        center: [0, 0],
        zoom: 2,
      }), */
      view: new View({
         center: fromLonLat([17.6791, 43.8563]), // Centered over Bosnia
         zoom: 8 // Zoom out slightly to see the boundaries and towns
       })

    });


    /* const FEATURES_WMS_TILE_LAYER = new TileLayer({
      source: new TileWMS({
        url: this.config.geoServerURL,
        params: { LAYERS: this.config.layerBaseId, TILED: true },
        serverType: "geoserver",
        transition: 0,
      }),
    }); */


    // feature search on map click
    this.map.on('singleclick', (evt) => {
      const features: any[] = [];

      this.map.forEachFeatureAtPixel(
        evt.pixel,
        (feature, layer) => {
          features.push(feature);
        },
        {
          hitTolerance: 5
        }
      );

      this.selectedFeatures = features.filter(feature => {
        const tsId = feature.get('tsid');

        if (this.config.workspaceName === this._config.geoServer.unWorkspaceName) {
          return true;
        }

        return tsId?.includes('BASELINE');
      })
        .map(feature => {

          const gmlName = feature.get('gml_name');

          return {
            featureId: feature.get('gml_identifier'),
            gmlName: !gmlName || gmlName === 'null' ? feature.get('gml_identifier') : gmlName.trim(),
            featureType: this.getFeatureType(feature.get('layer')) ?? '-'
          };

        });

    });

  }

  // load all official layer groups
  loadLayerGroups(): void {
    // get all layer group only for official data
    if (this.config.workspaceName == this._config.geoServer?.officialWorkspaceName) {
      this.mapChartService.getAllLayerGroup(this.config.workspaceName)
        .subscribe({
          next: async (layerGroups: LayerGroupDTO[]) => {

            // Use the layer groups here
            this.layerGroups = layerGroups;

            const officialFeatureStyle = await this.getOfficialFeatureStyle();

            // bild layers
            layerGroups.forEach(async group => {
              //console.log("Group name : " + group.name);
              // create a new VectorTileLayer for the layer group
              const layerName = `${this.config.workspaceName}:${group.name}`;
              const layer = this.createLayer(layerName);

              this.map.addLayer(layer)
    
              if(officialFeatureStyle != undefined) {
              const parsedStyle = this.createOfficialStyle(
                officialFeatureStyle,
                group.name
              );

              this.applyStyles(layer, parsedStyle);
            }
            });

          },
          error: (err) => {
            console.error('Failed to load layer groups', err);
          }
        });
    } else {
      // UN Layer Group
      const layerBaseId = this.config.workspaceName + ":" + this.data?.unId + "_UN_LayerGroup";
      const FEATURES_VECTOR_TILE_LAYER = this.createLayer(layerBaseId);

      FEATURES_VECTOR_TILE_LAYER.set('name', 'un_vector_tile_layer')
      this.map.addLayer(FEATURES_VECTOR_TILE_LAYER);
    }
  }

  // create vector tile layer for each layer group
  createLayer(layer): VectorTileLayer {
    const layerGropVectorLayer = new VectorTileLayer({
      declutter: true,
      source: new VectorTile({
        format: new MVT(),
        tileGrid: createXYZ(),
        tileUrlFunction: (tileCoord) => {
          const tileGrid = createXYZ();
          const bbox = tileGrid.getTileCoordExtent(tileCoord);

          return appendParams(this.config.geoServerURL, {
            SERVICE: "WMS",
            VERSION: "1.1.0",
            REQUEST: "GetMap",
            LAYERS: layer,
            FORMAT: "application/vnd.mapbox-vector-tile",
            SRS: "EPSG:3857",
            WIDTH: "512",
            HEIGHT: "512",
            BBOX: bbox.join(","),
            cacheBust: Date.now()
          });
        }
      })
    });

    return layerGropVectorLayer;
  }

  applyStyles(layerName: VectorTileLayer, parsedStyle: any): void {
    this.applyLayerStyle(layerName, parsedStyle);
  }

  private applyLayerStyle(layerName: VectorTileLayer, parsedStyle: any): void {
    applyStyle(layerName, parsedStyle, {
      updateSource: false,
      layers: this.STYLE_LAYERS,
    });
  }


  private createOfficialStyle(styleTemplate: any, layerGroupName: string): any {
    //const unId = layerGroupName.split("_")[1];
    const match = layerGroupName.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
    );

    if (!match) {
      //throw new Error(`Cannot extract UUID from layer group name: ${layerGroupName}`);
      console.error(`Cannot extract UUID from layer group name: ${layerGroupName}`);
      return null;
    }

    const unId = match[0];
    if (!styleTemplate) {
      console.error("styleTemplate is undefined");
      return null;
    }

    return JSON.parse(
      JSON.stringify(styleTemplate).replace(/{unId}/g, unId)
    );
  }

  private _getActiveLayersString(): string {
    return this.mapLayers
      .filter((layer) => layer.visible)
      .map((layer) => layer.id)
      .join(",");
  }

  // extract Feature Type from id
  getFeatureType(id: string): string {
    if (!id) return '-';

    // remove workspace + uuid prefix
    const parts = id.split('_');
    if (parts.length < 3) return id;

    const withoutPrefix = parts.slice(2).join('_');

    // remove .fid if exists
    //return withoutPrefix.split('.fid')[0];
    return withoutPrefix;
  }

  // close map Info panel
  closePanel(): void {
    this.selectedFeatures = [];
  }

  // navigate to relative input form on feature Name click on map Info
  openFeature(event: MouseEvent, feature: SelectedFeatureInfo): void {
    event.preventDefault();

    if (this.dialogRef) {
      this.dialogRef.close();
    }

    if (this.data?.unId != null) {
      this.router.navigate([
        '/info-management',
        feature.featureId,
        this.data.unId,
        this.data.unName,
        this.data.effectiveDate
      ]);
    } else {
      this.router.navigate([
        '/info-management',
        this.toPascalCase(feature.featureType),
        feature.featureId
      ]);
    }
  }

  // convert the feature type from snake case to pascal case
  toPascalCase(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  private async getOfficialFeatureStyle(): Promise<any> {
  const response = await fetch("/assets/map/official.feature.style.json");

  if (!response.ok) {
    throw new Error(`Failed to load official.feature.style.json: ${response.status}`);
  }
  console.log("official.feature.style.json loaded successfully");

  return response.json();
}

}
function showPopup(coordinate: Coordinate, hits: any[]) {
  throw new Error("Function not implemented.");
}



